/**
 * Lectures decoupled_toolbox propres à un domaine.
 *
 * La mécanique du contrat (URL, filtres, pagination, forme des items) vit dans
 * `services/api.js` — `decoupledCollection`, `decoupledSave`,
 * `decoupledDelete`, `unwrapSyncable`. Ce module ne porte que les requêtes
 * MÉTIER que plusieurs composants React se partagent, pour qu'aucun d'eux ne
 * réécrive le nom d'un champ Drupal ou la forme d'une réponse.
 *
 * Pourquoi du JS et pas du YAML : les trois premiers consommateurs sont deux
 * extensions TipTap (@mention) et une cellule d'édition en ligne — un plugin
 * ProseMirror ne peut pas être alimenté par un rjBuild.
 */
import { decoupledCollection, unwrapSyncable } from '../services/api';

export { unwrapSyncable as unwrap };

/**
 * Recherche de comptes pour les autocomplétions (@mention, cellule éditable).
 *
 * Remplace `GET /api/users/search` : la collection decoupled fait le même
 * travail et applique en plus l'access control de Drupal, que le contrôleur
 * contournait (`accessCheck(FALSE)`).
 *
 * `status: 1` écarte les comptes bloqués, et c'est AUSSI ce qui écarte
 * l'anonyme (uid 0, bloqué) : sans ce filtre il serait chargé puis refusé à
 * l'accès APRÈS le `limit`, et la page reviendrait plus courte que demandée —
 * un résultat qui manque, sans erreur.
 *
 * Différence assumée avec l'ancien endpoint : la recherche ne porte plus que
 * sur le nom. Elle couvrait aussi l'adresse e-mail, ce qu'un OU entre deux
 * champs exprimait en SQL et qu'une collection decoupled (des ET seulement) ne
 * sait pas dire.
 *
 * @param {string} query  Terme de recherche, éventuellement vide.
 * @param {number} limit  Nombre maximum de résultats.
 * @returns {Promise<Array<{uid: string, name: string}>>}
 */
export async function searchUsers(query, limit = 8) {
  const term = (query || '').trim();
  const rows = await decoupledCollection('user', 'user', {
    sort: 'name',
    limit,
    filters: [
      ['status', 1],
      ['name', term, 'CONTAINS'],
    ],
  });

  return rows
    .map(unwrapSyncable)
    .filter((u) => u && u.id != null && u.name)
    .map((u) => ({ uid: String(u.id), name: String(u.name) }));
}

/**
 * Les types d'objet du builder (`builder_def` de kind `object`).
 *
 * Remplace `GET /api/builder/object-defs`.
 *
 * `siteId` / `siteName` : le site auquel le type est rattaché (relation de
 * groupe, ajoutée au rendu par orbit_site — cf. DecoupledOutputSubscriber).
 * `null` = type commun à tous les sites (hérité d'avant le rattachement).
 *
 * @returns {Promise<Array<{nid: number, type: string, name: string, definition: string, siteId: number|null, siteName: string}>>}
 */
export async function fetchObjectDefs() {
  const rows = await decoupledCollection('node', 'builder_def', {
    sort: 'title',
    filters: [['field_def_kind', 'object']],
  });

  return rows
    .map(unwrapSyncable)
    .filter((d) => d && d.type)
    .map((d) => ({
      nid: Number(d.id),
      type: String(d.type),
      name: String(d.name ?? ''),
      definition: d.definition ?? '',
      siteId: d.siteId != null && d.siteId !== '' ? Number(d.siteId) : null,
      siteName: String(d.siteName ?? ''),
    }));
}

/** Le nid de la définition d'un type d'objet, depuis son machine name. */
async function objectDefNid(type) {
  const def = (await fetchObjectDefs()).find((d) => d.type === type);
  if (!def) throw new Error(`Object type '${type}' not found.`);
  return def.nid;
}

/**
 * Les instances d'un type d'objet, en forme de liste.
 *
 * Remplace `GET /api/builder/object/{type}/instances`. Le display employé est
 * `decoupled_list` et non `decoupled` : ce dernier embarque `build` et
 * `objectData`, ce qui pèse 27 Ko pour 21 articles là où la liste tient en
 * 3 Ko — et l'écart grandit avec le contenu de chaque instance.
 *
 * @returns {Promise<Array<{nid: number, slug: string, label: string}>>}
 */
export async function fetchObjectInstances(type) {
  const rows = await decoupledCollection('node', 'builder_unit', {
    display: 'decoupled_list',
    sort: 'title',
    filters: [['field_unit_template', await objectDefNid(type)]],
  });

  return rows.map(unwrapSyncable).map((o) => ({
    nid: Number(o.id),
    slug: String(o.slug ?? ''),
    label: String(o.name ?? ''),
  }));
}

/**
 * Une instance d'objet, adressée par son slug ou par son nid.
 *
 * Remplace `GET /api/builder/object/{type}/instance/{slugOrId}`. Un slug n'est
 * pas une adresse pour decoupled_toolbox (la route item ne connaît que l'id) :
 * il se résout par une collection filtrée, bornée au type demandé pour que
 * deux types ne puissent pas se voler un slug.
 *
 * @returns {Promise<object|null>} Le Syncable Object rendu, ou null si absent.
 */
export async function fetchObjectInstance(type, slugOrId) {
  const key = String(slugOrId);

  if (/^\d+$/.test(key)) {
    const res = await fetch(`/decoupled-api/node/builder_unit/${key}?display=decoupled`, {
      credentials: 'include',
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  const rows = await decoupledCollection('node', 'builder_unit', {
    limit: 1,
    filters: [
      ['field_unit_template', await objectDefNid(type)],
      ['field_unit_slug', key],
    ],
  });
  return rows.length > 0 ? rows[0] : null;
}
